import QRCode from "qrcode"
import { applyPalette, GIFEncoder, quantize } from "gifenc"
import type { Ape } from "./useOwnedApes"
import collection from "./data/collection.json"
import { backgroundMask, rarestTrait } from "./lib"

const SITE = "https://verify.boredapepastelclub.com"
const INK = "#2B2D42"
const MUTED = "#6B6E85"
const CORAL = "#FF8F7E"
const DISPLAY = "\"Chelsea Market\", cursive"
const BODY = "Inter, system-ui, sans-serif"

export const KINDS = {
	card: { label: "Share card", size: [1200, 675], ext: "png", hint: "For X, Discord or Telegram" },
	gif: { label: "Animated GIF", size: [480, 480], ext: "gif", hint: "Looping, for chats and replies" },
	wallpaper: { label: "Phone wallpaper", size: [1170, 2532], ext: "png", hint: "Lock and home screen" },
	banner: { label: "X banner", size: [1500, 500], ext: "png", hint: "Profile header, 1500 × 500" },
} as const
export type Kind = keyof typeof KINDS

const loadImage = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
	const img = new Image()
	img.crossOrigin = "anonymous" // Arweave sends CORS headers; without this the canvas can't be exported
	img.onload = () => resolve(img)
	img.onerror = () => reject(new Error("Couldn't load the ape image."))
	img.src = src
})

// Rounded rectangle path (fill it afterwards)
const round = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
	ctx.beginPath()
	ctx.roundRect(x, y, w, h, r)
}

// Shrinks the font until the text fits maxWidth
const fitText = (ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, size: number, family: string, weight = "") => {
	do ctx.font = `${weight} ${size--}px ${family}`
	while (ctx.measureText(text).width > maxWidth && size > 12)
	ctx.fillText(text, x, y)
}

const canvas2d = (w: number, h: number) => {
	const c = Object.assign(document.createElement("canvas"), { width: w, height: h })
	return [c, c.getContext("2d", { willReadFrequently: true })!] as const
}

// Looping sway + shine sweep over the ape's own background (it's a trait, so it stays)
const FRAMES = 36
const renderGif = (img: HTMLImageElement, size: number) => {
	// Cut the ape out of its flat background so it can move over a still one
	const [ape, actx] = canvas2d(size, size)
	actx.drawImage(img, 0, 0, size, size)
	const pixels = actx.getImageData(0, 0, size, size)
	const mask = backgroundMask(pixels.data, size, size)
	const [r, g, b] = pixels.data
	mask.forEach((bg, i) => { if (bg) pixels.data[i * 4 + 3] = 0 })
	actx.putImageData(pixels, 0, 0)

	const [, fctx] = canvas2d(size, size)
	const [layer, lctx] = canvas2d(size, size)
	const gif = GIFEncoder()
	for (let f = 0; f < FRAMES; f++) {
		const t = f / FRAMES
		fctx.fillStyle = `rgb(${r},${g},${b})`
		fctx.fillRect(0, 0, size, size)

		// Sway around the bottom centre. Shifted down 2.5% so the cut-off shirt never lifts off the edge
		// (a .03 rad tilt lifts the corners ~1.5%); the sides and top are background, so no gaps there.
		lctx.clearRect(0, 0, size, size)
		lctx.globalCompositeOperation = "source-over"
		lctx.save()
		lctx.translate(size / 2, size)
		lctx.rotate(Math.sin(t * 2 * Math.PI) * .03)
		lctx.drawImage(ape, -size / 2, -size + size * .025)
		lctx.restore()

		// Diagonal shine, only on the ape, during the first half of the loop
		const x = (t * 2 - .25) * size * 1.5
		const shine = lctx.createLinearGradient(x - size * .25, 0, x + size * .05, size * .3)
		shine.addColorStop(0, "rgba(255,255,255,0)")
		shine.addColorStop(.5, "rgba(255,255,255,.45)")
		shine.addColorStop(1, "rgba(255,255,255,0)")
		lctx.globalCompositeOperation = "source-atop"
		lctx.fillStyle = shine
		lctx.fillRect(0, 0, size, size)
		fctx.drawImage(layer, 0, 0)

		const { data } = fctx.getImageData(0, 0, size, size)
		const palette = quantize(data, 256)
		gif.writeFrame(applyPalette(data, palette), size, size, { palette, delay: 60 })
	}
	gif.finish()
	return new Blob([gif.bytes()], { type: "image/gif" })
}

export const render = async (ape: Ape, dna: string, kind: Kind): Promise<Blob> => {
	if (kind === "gif") return renderGif(await loadImage(ape.image), KINDS.gif.size[0])
	const [img] = await Promise.all([
		loadImage(ape.image),
		document.fonts.load(`64px ${DISPLAY}`),
		document.fonts.load(`600 24px ${BODY}`),
		document.fonts.load(`700 24px ${BODY}`),
	])
	const [w, h] = KINDS[kind].size
	const canvas = Object.assign(document.createElement("canvas"), { width: w, height: h })
	const ctx = canvas.getContext("2d")!

	// Every ape has a flat background, so its top-left pixel gives a seamless fill colour
	ctx.drawImage(img, 0, 0, 1, 1, 0, 0, 1, 1)
	const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data
	ctx.fillStyle = `rgb(${r},${g},${b})`
	ctx.fillRect(0, 0, w, h)

	const rank = `Rank ${collection.rank[ape.number] ?? "?"} / ${collection.total}`

	if (kind === "wallpaper") {
		// Ape at the bottom, the top stays clear for the lock-screen clock
		ctx.drawImage(img, 0, h - w, w, w)
	} else if (kind === "banner") {
		ctx.drawImage(img, w - h, 0, h, h)
		// X puts the profile picture over the bottom-left, so the text sits top-left
		ctx.fillStyle = "rgba(255,255,255,.92)"
		round(ctx, 60, 60, 760, 230, 32)
		ctx.fill()
		ctx.fillStyle = INK
		fitText(ctx, ape.name, 100, 160, 680, 64, DISPLAY)
		ctx.fillStyle = MUTED
		fitText(ctx, `Bored Ape Pastel Club · ${rank}`, 100, 230, 680, 30, BODY, "600")
	} else {
		ctx.drawImage(img, 0, 0, h, h)
		const x = h + 40, pw = w - x - 40
		ctx.fillStyle = "rgba(255,255,255,.94)"
		round(ctx, x, 40, pw, h - 80, 32)
		ctx.fill()

		const left = x + 40, inner = pw - 80
		ctx.fillStyle = MUTED
		fitText(ctx, "BORED APE PASTEL CLUB", left, 115, inner, 22, BODY, "700")
		ctx.fillStyle = INK
		fitText(ctx, ape.name, left, 190, inner, 60, DISPLAY)

		// Coral rank pill
		ctx.font = `700 26px ${BODY}`
		const pill = ctx.measureText(rank).width + 40
		ctx.fillStyle = CORAL
		round(ctx, left, 225, pill, 50, 25)
		ctx.fill()
		ctx.fillStyle = INK
		ctx.fillText(rank, left + 20, 259)

		const rare = rarestTrait(ape.attributes, collection.counts, collection.total)
		if (rare) {
			ctx.fillStyle = MUTED
			fitText(ctx, "RAREST TRAIT", left, 335, inner, 20, BODY, "700")
			ctx.fillStyle = INK
			fitText(ctx, `${rare.trait_type}: ${rare.value}`, left, 375, inner, 32, BODY, "700")
			ctx.fillStyle = MUTED
			fitText(ctx, `Only ${rare.pct.toFixed(1)}% have this`, left, 412, inner, 24, BODY, "600")
		}

		// QR to the ape's public page, same link as the tag on the ape
		const qr = document.createElement("canvas")
		await QRCode.toCanvas(qr, `${SITE}/${dna}`, { width: 150, margin: 1, color: { dark: INK } })
		const qy = h - 40 - 40 - 150 // panel bottom, minus padding, minus QR
		ctx.drawImage(qr, left, qy)
		ctx.fillStyle = MUTED
		fitText(ctx, "Scan to verify", left + 170, qy + 65, inner - 170, 22, BODY, "600")
		fitText(ctx, "verify.boredapepastelclub.com", left + 170, qy + 95, inner - 170, 18, BODY)
	}

	return new Promise((resolve, reject) =>
		canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error("Couldn't create the image.")), "image/png"))
}
